/**
 * 🔒 The Item page's Transactions tile (T1254; decision 1940): every line that
 * names the Item, newest first, from the document lines, the bills of
 * materials, the jobs' fitted parts and the stock ledger, with one chip per
 * side and twenty rows a page. 🔨 Nothing here is stored; the API assembles the
 * rows from the tables that hold them and this module names their shape.
 */

/** The four chips, All first. */
export const ITEM_TRANSACTION_FILTERS = ['all', 'sales', 'purchases', 'stock'] as const;
export type ItemTransactionFilter = (typeof ITEM_TRANSACTION_FILTERS)[number];

/** The side a row stands on: a chip names a side, and All shows the three. */
export type ItemTransactionSide = Exclude<ItemTransactionFilter, 'all'>;

/** How many rows a page of the tile holds, and how many Show more brings. */
export const ITEM_TRANSACTIONS_PAGE = 20;

/** Where the tile reads its rows, under the catalog's own root. */
export function itemTransactionsPath(itemId: string, filter: ItemTransactionFilter = 'all', offset = 0): string {
  const query = new URLSearchParams({ filter, offset: String(offset), limit: String(ITEM_TRANSACTIONS_PAGE) });
  return `/catalog/items/${itemId}/transactions?${query.toString()}`;
}

export function isItemTransactionFilter(value: unknown): value is ItemTransactionFilter {
  return typeof value === 'string' && (ITEM_TRANSACTION_FILTERS as readonly string[]).includes(value);
}

/**
 * 🔒 Which side a document kind stands on: what the business sold or quoted,
 * what it bought or asked a price for. A Work Order's fitted part is sales, the
 * job it was sold on; its stock movement is a stock row of its own.
 */
export const ITEM_TRANSACTION_SIDES: Readonly<Record<string, ItemTransactionSide>> = {
  Quote: 'sales',
  SalesOrder: 'sales',
  Invoice: 'sales',
  CreditNote: 'sales',
  ChangeOrder: 'sales',
  WorkOrder: 'sales',
  BOM: 'sales',
  PurchaseOrder: 'purchases',
  Bill: 'purchases',
  RFQ: 'purchases',
};

/** The side of a kind, a kind the map does not name reading as stock. */
export function itemTransactionSide(kind: string): ItemTransactionSide {
  return ITEM_TRANSACTION_SIDES[kind] ?? 'stock';
}

/** One row of the tile, as the API sends it. Money is cents and reaches a person through the money formatter. */
export interface ItemTransactionRow {
  /** The line's or the movement's own id. */
  id: string;
  side: ItemTransactionSide;
  /** The document's or the movement's day, `YYYY-MM-DD`; a list with no date of its own reads null. */
  date: string | null;
  /** The kind in words: the document's Title Case name, or the movement's reason. */
  kind: string;
  /** The document's number, or null where it has none yet. */
  number: string | null;
  /** The document's page, where it has one. */
  route: string | null;
  /** The account the document is with: the customer, the vendor, or the site a movement moved at. */
  account: string | null;
  accountRoute: string | null;
  quantity: number;
  unit: string;
  /** Null on an RFQ line before its answer, and on a cost withheld from this viewer (decision 0075). */
  unitPriceCents: number | null;
  /** The document's status pill; a stock movement carries none. */
  status: { text: string; group: 'good' | 'warning' | 'error' | 'inactive' } | null;
  /**
   * 🔒 The id of the row this one came from, where its record names one (decision 8880): a Bill line's order
   * line, an Invoice line's Sales Order line, a fitted part's order line, the line a movement moved. The tile
   * nests the row under that row where it is on the same page.
   */
  sourceLineId: string | null;
}

/** The tile's answer: one page of rows, the count behind each chip, and whether this viewer sees cost. */
export interface ItemTransactionsAnswer {
  rows: ItemTransactionRow[];
  /** Rows on the chip asked for, all pages together. */
  total: number;
  counts: Record<ItemTransactionFilter, number>;
  seesCost: boolean;
  currencyCode: string;
}

/**
 * 🔒 The rows of one chip, newest first, a row with no date last, and one page
 * of them. Rows on the same day keep the order they were handed in, which the
 * API makes the document's own order.
 */
export function itemTransactionsPage(rows: readonly ItemTransactionRow[], filter: ItemTransactionFilter, offset: number, limit: number = ITEM_TRANSACTIONS_PAGE): { rows: ItemTransactionRow[]; total: number; counts: Record<ItemTransactionFilter, number> } {
  const sorted = rows.map((row, index) => ({ row, index })).sort((a, b) => {
    if (a.row.date === b.row.date) return a.index - b.index;
    if (a.row.date === null) return 1;
    if (b.row.date === null) return -1;
    return a.row.date < b.row.date ? 1 : -1;
  }).map(({ row }) => row);
  const counts = { all: rows.length, sales: 0, purchases: 0, stock: 0 };
  for (const row of rows) counts[row.side] += 1;
  const chosen = filter === 'all' ? sorted : sorted.filter((row) => row.side === filter);
  const start = Math.max(0, Math.floor(offset));
  return { rows: chosen.slice(start, start + Math.max(1, Math.floor(limit))), total: chosen.length, counts };
}
